const MAIL_REMOTE_IMAGE_CACHE_DB_NAME = 'streamlain-mail-remote-image-cache';
const MAIL_REMOTE_IMAGE_CACHE_STORE_NAME = 'remoteImages';
const MAIL_REMOTE_IMAGE_CACHE_DB_VERSION = 1;
const MAIL_REMOTE_IMAGE_CACHE_KEY_PREFIX = 'mail-remote-image:';
const MAIL_REMOTE_IMAGE_CACHE_MAX_ITEMS = 500;
const MAIL_REMOTE_IMAGE_CACHE_MAX_BYTES = 50 * 1024 * 1024;
const MAIL_REMOTE_IMAGE_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

type MailRemoteImageCacheEntry = {
  key: string;
  data: Uint8Array;
  mime: string;
  createdAt: string;
  size: number;
};

let mailRemoteImageCacheDbPromise: Promise<IDBDatabase | null> | null = null;

export function readBlobAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Mail remote image could not be decoded'));
      }
    });
    reader.addEventListener('error', () => {
      reject(reader.error ?? new Error('Mail remote image could not be read'));
    });
    reader.readAsDataURL(blob);
  });
}

export function getMailRemoteImageCacheKey(input: {
  workspaceId: string;
  messageId: string;
  imageId: string;
}): string {
  return `${MAIL_REMOTE_IMAGE_CACHE_KEY_PREFIX}${input.workspaceId}:${input.messageId}:${input.imageId}`;
}

export async function readCachedMailRemoteImageDataUrl(
  cacheKey: string
): Promise<string | null> {
  try {
    const cached = await getMailRemoteImageCacheEntry(cacheKey);
    if (!cached) return null;
    const createdAtMs = mailRemoteImageCacheTimestamp(cached.createdAt);
    if (Date.now() - createdAtMs > MAIL_REMOTE_IMAGE_CACHE_TTL_MS) {
      await deleteMailRemoteImageCacheEntry(cacheKey);
      return null;
    }
    await setMailRemoteImageCacheEntry({
      ...cached,
      createdAt: new Date().toISOString(),
    });
    return await readBlobAsDataUrl(
      new Blob([new Uint8Array(cached.data)], {
        type: cached.mime || 'application/octet-stream',
      })
    );
  } catch {
    return null;
  }
}

export async function writeCachedMailRemoteImage(
  cacheKey: string,
  blob: Blob
): Promise<void> {
  try {
    const data = new Uint8Array(await blob.arrayBuffer());
    await setMailRemoteImageCacheEntry({
      key: cacheKey,
      data,
      mime: blob.type || 'application/octet-stream',
      createdAt: new Date().toISOString(),
      size: data.byteLength,
    });
    await pruneCachedMailRemoteImages();
  } catch {
    // Device-local image cache is best effort; preview still uses fetched data.
  }
}

export async function deleteMailRemoteImageCacheEntriesForWorkspace(
  workspaceId: string
): Promise<void> {
  const prefix = `${MAIL_REMOTE_IMAGE_CACHE_KEY_PREFIX}${workspaceId}:`;
  const entries = await listMailRemoteImageCacheEntries();
  await Promise.all(
    entries
      .filter(entry => entry.key.startsWith(prefix))
      .map(entry => deleteMailRemoteImageCacheEntry(entry.key))
  );
}

function mailRemoteImageCacheTimestamp(
  value: Date | string | undefined
): number {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') {
    const timestamp = Date.parse(value);
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }
  return 0;
}

async function pruneCachedMailRemoteImages(): Promise<void> {
  try {
    const entries = (await listMailRemoteImageCacheEntries())
      .filter(entry => entry.key.startsWith(MAIL_REMOTE_IMAGE_CACHE_KEY_PREFIX))
      .sort(
        (left, right) =>
          mailRemoteImageCacheTimestamp(right.createdAt) -
          mailRemoteImageCacheTimestamp(left.createdAt)
      );
    let retainedBytes = 0;
    const now = Date.now();
    await Promise.all(
      entries.map(async (entry, index) => {
        retainedBytes += entry.size;
        if (
          index >= MAIL_REMOTE_IMAGE_CACHE_MAX_ITEMS ||
          retainedBytes > MAIL_REMOTE_IMAGE_CACHE_MAX_BYTES ||
          now - mailRemoteImageCacheTimestamp(entry.createdAt) >
            MAIL_REMOTE_IMAGE_CACHE_TTL_MS
        ) {
          await deleteMailRemoteImageCacheEntry(entry.key);
        }
      })
    );
  } catch {
    // Cache eviction is best effort; never block mail rendering on it.
  }
}

function openMailRemoteImageCacheDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  mailRemoteImageCacheDbPromise ??= new Promise(resolve => {
    const request = indexedDB.open(
      MAIL_REMOTE_IMAGE_CACHE_DB_NAME,
      MAIL_REMOTE_IMAGE_CACHE_DB_VERSION
    );
    request.addEventListener('upgradeneeded', () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(MAIL_REMOTE_IMAGE_CACHE_STORE_NAME)) {
        db.createObjectStore(MAIL_REMOTE_IMAGE_CACHE_STORE_NAME, {
          keyPath: 'key',
        });
      }
    });
    request.addEventListener('success', () => resolve(request.result));
    request.addEventListener('error', () => resolve(null));
    request.addEventListener('blocked', () => resolve(null));
  });
  return mailRemoteImageCacheDbPromise;
}

async function withMailRemoteImageCacheStore<T>(
  mode: IDBTransactionMode,
  callback: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T | null> {
  const db = await openMailRemoteImageCacheDb();
  if (!db) return null;
  return new Promise(resolve => {
    const transaction = db.transaction(
      MAIL_REMOTE_IMAGE_CACHE_STORE_NAME,
      mode
    );
    const request = callback(
      transaction.objectStore(MAIL_REMOTE_IMAGE_CACHE_STORE_NAME)
    );
    request.addEventListener('success', () => resolve(request.result ?? null));
    request.addEventListener('error', () => resolve(null));
    transaction.addEventListener('error', () => resolve(null));
  });
}

function getMailRemoteImageCacheEntry(
  cacheKey: string
): Promise<MailRemoteImageCacheEntry | null> {
  return withMailRemoteImageCacheStore<MailRemoteImageCacheEntry>(
    'readonly',
    store => store.get(cacheKey)
  );
}

async function setMailRemoteImageCacheEntry(
  entry: MailRemoteImageCacheEntry
): Promise<void> {
  await withMailRemoteImageCacheStore('readwrite', store => store.put(entry));
}

async function deleteMailRemoteImageCacheEntry(
  cacheKey: string
): Promise<void> {
  await withMailRemoteImageCacheStore('readwrite', store =>
    store.delete(cacheKey)
  );
}

async function listMailRemoteImageCacheEntries(): Promise<
  MailRemoteImageCacheEntry[]
> {
  return (
    (await withMailRemoteImageCacheStore<MailRemoteImageCacheEntry[]>(
      'readonly',
      store => store.getAll()
    )) ?? []
  );
}
